import React, { useState, useEffect } from 'react';
import { supabase_get_client_settings, supabase_update_client_settings, extractErrorMessage } from '../services/supabaseService';
import { Loader2, AlertCircle, CheckCircle2, ListPlus, X, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface PlacementTypesSettingsTabProps {
    clientId: string;
}

export const PlacementTypesSettingsTab: React.FC<PlacementTypesSettingsTabProps> = ({ clientId }) => {
    const [settings, setSettings] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [placementTypes, setPlacementTypes] = useState<string[]>([]);
    const [employmentTypes, setEmploymentTypes] = useState<string[]>([]);
    const [contractTypes, setContractTypes] = useState<string[]>([]);

    useEffect(() => {
        if (clientId) fetchSettings();
    }, [clientId]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const data = await supabase_get_client_settings(clientId);
            setSettings(data || {});
            
            setPlacementTypes(data?.placement_types || ['Deta-vast', 'Detachering', 'Freelance', 'Werving & Selectie']);
            setEmploymentTypes(data?.employment_types || ['Voltijd', 'Deeltijd', 'Oproep', 'Stage']);
            setContractTypes(data?.contract_types || ['Bepaalde Tijd', 'Onbepaalde Tijd', 'Projectbasis']);
            
        } catch (e: any) {
            console.error("Error fetching client settings:", e);
            setError(extractErrorMessage(e));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (key: string, updatedList: string[]) => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const newSettings = { ...settings, [key]: updatedList };
            await supabase_update_client_settings(clientId, newSettings);
            setSettings(newSettings);
            setSuccess("Instellingen succesvol opgeslagen!");
            setTimeout(() => setSuccess(null), 3000);
        } catch (e: any) {
            setError(extractErrorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-8 animate-fadeIn">
            <div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">Plaatsing Instellingen</h2>
                <p className="text-sm text-slate-500 font-medium mt-1">
                    Beheer de verschillende categorieën voor plaatsingen. Deze opties worden getoond in het plaatsingen overzicht.
                </p>
                {error && <div className="bg-red-50 border border-red-100 text-red-600 p-4 rounded-xl flex items-center gap-3 mt-4"><AlertCircle size={20} /><span className="text-xs font-bold">{error}</span></div>}
                {success && <div className="bg-emerald-50 border border-emerald-100 text-emerald-600 p-4 rounded-xl flex items-center gap-3 mt-4"><CheckCircle2 size={20} /><span className="text-xs font-bold">{success}</span></div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <SortableListEditor 
                    title="Type Plaatsing" 
                    placeholder="bijv. Interim"
                    items={placementTypes} 
                    onUpdate={(items) => { setPlacementTypes(items); handleSave('placement_types', items); }} 
                    isSaving={isSaving}
                    id="placement-types"
                />
                <SortableListEditor 
                    title="Type Dienstverband" 
                    placeholder="bijv. Oproep"
                    items={employmentTypes} 
                    onUpdate={(items) => { setEmploymentTypes(items); handleSave('employment_types', items); }} 
                    isSaving={isSaving}
                    id="employment-types"
                />
                <SortableListEditor 
                    title="Type Contract" 
                    placeholder="bijv. Bepaalde Tijd"
                    items={contractTypes} 
                    onUpdate={(items) => { setContractTypes(items); handleSave('contract_types', items); }} 
                    isSaving={isSaving}
                    id="contract-types"
                />
            </div>
        </div>
    );
};

interface SortableListEditorProps {
    id: string;
    title: string;
    placeholder: string;
    items: string[];
    onUpdate: (items: string[]) => void;
    isSaving: boolean;
}

const SortableListEditor: React.FC<SortableListEditorProps> = ({ id, title, placeholder, items, onUpdate, isSaving }) => {
    const [newItem, setNewItem] = useState('');

    const handleAdd = () => {
        if (!newItem.trim()) return;
        if (items.includes(newItem.trim())) return;
        
        onUpdate([...items, newItem.trim()]);
        setNewItem('');
    };

    const handleRemove = (index: number) => {
        const updated = items.filter((_, idx) => idx !== index);
        onUpdate(updated);
    };

    const onDragEnd = (result: any) => {
        if (!result.destination) return;
        const newItems = Array.from(items);
        const [reorderedItem] = newItems.splice(result.source.index, 1);
        newItems.splice(result.destination.index, 0, reorderedItem);
        onUpdate(newItems);
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 overflow-hidden flex flex-col h-full">
            <h3 className="font-black text-slate-800 mb-4">{title}</h3>
            
            <div className="flex gap-2 mb-4">
                <input 
                    type="text" 
                    value={newItem} 
                    onChange={(e) => setNewItem(e.target.value)} 
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                    placeholder={placeholder} 
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-slate-400"
                />
                <button 
                    onClick={handleAdd} 
                    disabled={!newItem.trim() || isSaving}
                    className="px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-black transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                    <PlusIcon size={16} />
                </button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={id}>
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1.5 flex-1 overflow-y-auto">
                            {items.map((item, index) => (
                                <Draggable key={item} draggableId={`${id}-${item}`} index={index}>
                                    {(provided) => (
                                        <div 
                                            ref={provided.innerRef} 
                                            {...provided.draggableProps} 
                                            className="flex items-center justify-between p-2.5 bg-white border border-slate-100 rounded-lg shadow-sm hover:border-slate-200 hover:shadow-md transition-all group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div {...provided.dragHandleProps} className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing">
                                                    <GripVertical size={14} />
                                                </div>
                                                <span className="text-xs font-bold text-slate-900">{item}</span>
                                            </div>
                                            <button 
                                                onClick={() => handleRemove(index)}
                                                className="w-6 h-6 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {items.length === 0 && (
                <div className="text-center py-6 text-slate-400 font-medium text-xs">
                    Geen items gevonden.
                </div>
            )}
        </div>
    );
};

const PlusIcon = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);
